import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Clock, 
  FileText, 
  Shield,
  ArrowRight,
  Info
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NewRequest() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    purpose: '',
    term_months: '12',
    collateral_type: '',
    collateral_amount: '',
    preferred_stablecoin: ''
  });

  const purposes = [
    'Business Expansion',
    'Equipment Purchase',
    'Working Capital',
    'Debt Consolidation',
    'Real Estate',
    'Personal',
    'Other'
  ];

  const terms = [
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '12', label: '12 months' },
    { value: '24', label: '24 months' },
    { value: '36', label: '36 months' },
    { value: '48', label: '48 months' },
    { value: '60', label: '60 months' }
  ];

  const collateralTypes = [
    'Crypto (ETH)',
    'Crypto (KOJIRU)',
    'NFT',
    'None'
  ];

  const stablecoins = [
    { value: 'USD', label: 'USD — US Dollar' },
    { value: 'USDC', label: 'USDC — USD Coin (Circle)' },
    { value: 'USDT', label: 'USDT — Tether' },
    { value: 'DAI', label: 'DAI — MakerDAO' },
    { value: 'USAT', label: 'USAT — Tether U.S. Stablecoin' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    if (!formData.purpose) {
      toast({ title: 'Error', description: 'Please select a purpose', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        purpose: formData.purpose,
        term_months: parseInt(formData.term_months),
        collateral_type: formData.collateral_type || null,
        collateral_amount: formData.collateral_amount ? parseFloat(formData.collateral_amount) : null,
        preferred_stablecoin: formData.preferred_stablecoin || null
      };

      await axios.post(`${API}/loans/requests`, payload);
      
      toast({
        title: 'Success!',
        description: 'Your loan request has been submitted. Lenders can now place bids.'
      });
      
      navigate('/consumer/requests');
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to create request',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="New Loan Request">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                Create Request for Bid (RFB)
              </CardTitle>
              <CardDescription>
                Submit your loan request and let lenders compete to offer you the best rates.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Amount */}
                <div>
                  <Label htmlFor="amount">Loan Amount (USD)</Label>
                  <div className="relative mt-1.5">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="amount"
                      data-testid="loan-amount-input"
                      type="number"
                      placeholder="10000"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="pl-10 h-12 bg-white/5 border-white/10"
                      min="100"
                      max="1000000"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Minimum: $100, Maximum: $1,000,000</p>
                </div>

                {/* Purpose */}
                <div>
                  <Label htmlFor="purpose">Loan Purpose</Label>
                  <Select 
                    value={formData.purpose} 
                    onValueChange={(v) => setFormData({ ...formData, purpose: v })}
                  >
                    <SelectTrigger data-testid="loan-purpose-select" className="h-12 bg-white/5 border-white/10 mt-1.5">
                      <SelectValue placeholder="Select purpose" />
                    </SelectTrigger>
                    <SelectContent>
                      {purposes.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Term */}
                <div>
                  <Label htmlFor="term">Loan Term</Label>
                  <Select 
                    value={formData.term_months} 
                    onValueChange={(v) => setFormData({ ...formData, term_months: v })}
                  >
                    <SelectTrigger data-testid="loan-term-select" className="h-12 bg-white/5 border-white/10 mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {terms.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Stablecoin */}
                <div>
                  <Label htmlFor="stablecoin">Preferred Settlement Currency (Optional)</Label>
                  <Select 
                    value={formData.preferred_stablecoin} 
                    onValueChange={(v) => setFormData({ ...formData, preferred_stablecoin: v })}
                  >
                    <SelectTrigger data-testid="stablecoin-select" className="h-12 bg-white/5 border-white/10 mt-1.5">
                      <SelectValue placeholder="No preference — lender decides" />
                    </SelectTrigger>
                    <SelectContent>
                      {stablecoins.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Lenders choose which currency to fund your loan with. You can indicate a preference.</p>
                </div>

                {/* Collateral */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-medium">Collateral (Optional)</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Adding collateral can help you get better rates from lenders.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Collateral Type</Label>
                      <Select 
                        value={formData.collateral_type} 
                        onValueChange={(v) => setFormData({ ...formData, collateral_type: v })}
                      >
                        <SelectTrigger data-testid="collateral-type-select" className="h-10 bg-white/5 border-white/10 mt-1.5">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {collateralTypes.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.collateral_type && formData.collateral_type !== 'None' && (
                      <div>
                        <Label>Collateral Amount</Label>
                        <Input
                          data-testid="collateral-amount-input"
                          type="number"
                          placeholder="Amount"
                          value={formData.collateral_amount}
                          onChange={(e) => setFormData({ ...formData, collateral_amount: e.target.value })}
                          className="h-10 bg-white/5 border-white/10 mt-1.5"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/10 border border-primary/20">
                  <Info className="w-5 h-5 text-primary mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-primary mb-1">How it works</p>
                    <p className="text-muted-foreground">
                      Once submitted, your request will be visible to all lenders on the platform. 
                      They will compete by placing bids with their best interest rates. 
                      You can accept any bid that suits your needs.
                    </p>
                  </div>
                </div>

                {/* Submit */}
                <Button
                  data-testid="submit-request-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-lg gradient-primary"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      Submit Request
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
